import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import type { BugReportInsert, FeatureRequestInsert } from "@/lib/productFeedback";
import { supabase } from "@/integrations/supabase/client";
import { buildFeatureAutomationFallback } from "../../supabase/functions/_shared/featureAutomation";
import { useAuth } from "./useAuth";

interface FeatureRequestRow {
  id: string;
  summary: string;
  details: string | null;
  route: string;
  reporter_role: "coach" | "leader" | "admin";
  reporter_user_id: string;
  decision_status: "new" | "go" | "no-go";
  decision_notes: string | null;
  decided_by: string | null;
  decided_at: string | null;
  created_at: string;
}

interface FeatureRequestRunRow {
  id: string;
  feature_request_id: string;
  status: "new" | "summarized" | "go" | "no-go" | "spec_created" | "plan_created" | "in_build" | "done" | "blocked";
  automation_summary: string | null;
  automation_recommendation: string | null;
  affected_surfaces: string[] | null;
  scope_guess: "small" | "medium" | "large" | null;
  problem_framing: string | null;
  spec_markdown: string | null;
  plan_markdown: string | null;
  failure_reason: string | null;
  created_at: string;
  updated_at: string;
}

interface BugReportRow {
  id: string;
  summary: string;
  details: string | null;
  route: string;
  reporter_role: "coach" | "leader" | "admin";
  reporter_user_id: string;
  status: string;
  github_issue_number: number | null;
  github_issue_url: string | null;
  automation_queue_error?: string | null;
  screenshot_bucket: string | null;
  screenshot_path: string | null;
  screenshot_name: string | null;
  created_at: string;
}

interface ReporterProfileRow {
  id: string;
  full_name: string | null;
  email: string | null;
}

type BugFixRunStatus =
  | "new"
  | "duplicate"
  | "go"
  | "no-go"
  | "queued"
  | "threshold_passed"
  | "threshold_blocked"
  | "github_issue_created"
  | "approved_for_auto_fix"
  | "execution_handoff_created"
  | "fix_pr_opened"
  | "ci_passed"
  | "merged"
  | "deployed"
  | "verified_fixed"
  | "closed"
  | string;

interface BugFixRunRow {
  id: string;
  bug_report_id: string | null;
  canonical_bug_report_id: string | null;
  status: BugFixRunStatus;
  created_at: string;
  updated_at: string;
}

export interface BugReportReviewItem {
  id: string;
  summary: string;
  details: string | null;
  route: string;
  reporterRole: "coach" | "leader" | "admin";
  reporterUserId: string;
  reporterName: string | null;
  reporterEmail: string | null;
  status: string;
  automationStatus: BugFixRunStatus | null;
  githubIssueNumber: number | null;
  githubIssueUrl: string | null;
  automationQueueError: string | null;
  screenshotBucket: string | null;
  screenshotPath: string | null;
  screenshotName: string | null;
  createdAt: string;
}

export interface FeatureRequestReviewItem {
  id: string;
  summary: string;
  details: string | null;
  route: string;
  reporterRole: "coach" | "leader" | "admin";
  reporterUserId: string;
  decisionStatus: "new" | "go" | "no-go";
  decisionNotes: string | null;
  decidedBy: string | null;
  decidedAt: string | null;
  createdAt: string;
  runStatus: FeatureRequestRunRow["status"] | null;
  automationSummary: string | null;
  automationRecommendation: string | null;
  affectedSurfaces: string[];
  scopeGuess: FeatureRequestRunRow["scope_guess"];
  problemFraming: string | null;
  specMarkdown: string | null;
  planMarkdown: string | null;
  failureReason: string | null;
}

export interface DecideFeatureRequestInput {
  id: string;
  decisionStatus: "go" | "no-go";
  decisionNotes?: string | null;
}

export interface SubmitBugReportResult extends BugReportInsert {
  screenshotMetadataPersisted: boolean;
}

const FEATURE_REQUESTS_QUERY_KEY = ["feature-requests-review-queue"] as const;
const BUG_REPORTS_QUERY_KEY = ["bug-reports-review-queue"] as const;

function mapBugReportRow(
  row: BugReportRow,
  run: BugFixRunRow | null = null,
  reporterProfile: ReporterProfileRow | null = null,
): BugReportReviewItem {
  return {
    id: row.id,
    summary: row.summary,
    details: row.details,
    route: row.route,
    reporterRole: row.reporter_role,
    reporterUserId: row.reporter_user_id,
    reporterName: reporterProfile?.full_name ?? null,
    reporterEmail: reporterProfile?.email ?? null,
    status: row.status,
    automationStatus: run?.status ?? null,
    githubIssueNumber: row.github_issue_number,
    githubIssueUrl: row.github_issue_url,
    automationQueueError: row.automation_queue_error ?? null,
    screenshotBucket: row.screenshot_bucket,
    screenshotPath: row.screenshot_path,
    screenshotName: row.screenshot_name,
    createdAt: row.created_at,
  };
}

function mapFeatureRequestRow(
  row: FeatureRequestRow,
  run: FeatureRequestRunRow | null,
): FeatureRequestReviewItem {
  return {
    id: row.id,
    summary: row.summary,
    details: row.details,
    route: row.route,
    reporterRole: row.reporter_role,
    reporterUserId: row.reporter_user_id,
    decisionStatus: row.decision_status,
    decisionNotes: row.decision_notes,
    decidedBy: row.decided_by,
    decidedAt: row.decided_at,
    createdAt: row.created_at,
    runStatus: run?.status ?? null,
    automationSummary: run?.automation_summary ?? null,
    automationRecommendation: run?.automation_recommendation ?? null,
    affectedSurfaces: Array.isArray(run?.affected_surfaces) ? run.affected_surfaces : [],
    scopeGuess: run?.scope_guess ?? null,
    problemFraming: run?.problem_framing ?? null,
    specMarkdown: run?.spec_markdown ?? null,
    planMarkdown: run?.plan_markdown ?? null,
    failureReason: run?.failure_reason ?? null,
  };
}

function hasInternalReviewRole(roles: string[]) {
  return roles.includes("coach") || roles.includes("admin");
}

function hasBugReportReviewRole(roles: string[]) {
  return roles.includes("admin");
}

export function useSubmitBugReport() {
  return useMutation({
    mutationFn: async (payload: BugReportInsert): Promise<SubmitBugReportResult> => {
      const insertPayload = {
        summary: payload.summary,
        details: payload.details,
        route: payload.route,
        reporter_role: payload.reporter_role,
        reporter_user_id: payload.reporter_user_id,
        user_agent: payload.user_agent,
        client_log_bundle: payload.client_log_bundle,
        status: "submitted" as const,
        created_at: payload.created_at,
      };

      const { data, error } = await supabase.from("bug_reports").insert(insertPayload).select("id").single();
      if (error) throw error;

      const bugReportId = data?.id;
      let screenshotMetadataPersisted = !payload.screenshot_bucket || !payload.screenshot_path;

      if (bugReportId) {
        if (payload.screenshot_bucket && payload.screenshot_path) {
          const { error: screenshotMetadataError } = await supabase
            .from("bug_reports")
            .update({
              screenshot_bucket: payload.screenshot_bucket,
              screenshot_path: payload.screenshot_path,
              screenshot_name: payload.screenshot_name,
              screenshot_mime_type: payload.screenshot_mime_type,
              screenshot_size_bytes: payload.screenshot_size_bytes,
            })
            .eq("id", bugReportId);

          if (screenshotMetadataError) {
            console.error("[useSubmitBugReport] Failed to persist bug report screenshot metadata", screenshotMetadataError);
          } else {
            screenshotMetadataPersisted = true;
          }
        }

        const { error: invokeError } = await supabase.functions.invoke("queue-bug-automation", {
          body: { bugReportId },
        });

        if (invokeError) {
          const { error: updateError } = await supabase
            .from("bug_reports")
            .update({
              automation_queue_error: invokeError.message.slice(0, 500),
            })
            .eq("id", bugReportId);

          if (updateError) {
            console.error("[useSubmitBugReport] Failed to persist automation queue error", updateError);
          }
        }
      }

      return { ...payload, screenshotMetadataPersisted };
    },
  });
}

export function useSubmitFeatureRequest() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: FeatureRequestInsert) => {
      const insertPayload = {
        summary: payload.summary,
        details: payload.details,
        route: payload.route,
        reporter_role: payload.reporter_role,
        reporter_user_id: payload.reporter_user_id,
        decision_status: "new" as const,
        created_at: payload.created_at,
      };

      const { data, error } = await supabase.from("feature_requests").insert(insertPayload).select("id").single();
      if (error) throw error;

      const featureRequestId = data?.id;
      if (featureRequestId) {
        const { error: invokeError } = await supabase.functions.invoke("queue-feature-automation", {
          body: { featureRequestId },
        });

        if (invokeError) {
          console.error("[useSubmitFeatureRequest] Failed to queue feature automation", invokeError);

          const fallbackSummary = buildFeatureAutomationFallback({
            summary: payload.summary,
            details: payload.details,
            route: payload.route,
            reporterRole: payload.reporter_role,
          });

          const { error: fallbackInsertError } = await supabase.from("feature_request_runs").upsert(
            {
              feature_request_id: featureRequestId,
              status: "summarized",
              automation_summary: fallbackSummary.automationSummary,
              automation_recommendation: fallbackSummary.automationRecommendation,
              affected_surfaces: fallbackSummary.affectedSurfaces,
              scope_guess: fallbackSummary.scopeGuess,
              problem_framing: fallbackSummary.problemFraming,
              github_issue_number: null,
              github_issue_url: null,
              github_sync_status: null,
              github_sync_error: null,
              decision_source: null,
              harness_stage: null,
              failure_reason: `queue-feature-automation failed: ${invokeError.message.slice(0, 500)}`,
            },
            {
              onConflict: "feature_request_id",
            },
          );

          if (fallbackInsertError) {
            console.error(
              "[useSubmitFeatureRequest] Failed to recover feature automation after queue error",
              fallbackInsertError,
            );
          }
        }

        const { error: githubIssueError } = await supabase.functions.invoke("create-github-feature-issue", {
          body: { featureRequestId },
        });

        if (githubIssueError) {
          console.error("[useSubmitFeatureRequest] Failed to create feature GitHub issue", githubIssueError);

          const { error: updateError } = await supabase
            .from("feature_request_runs")
            .update({
              github_sync_status: "failed",
              github_sync_error: githubIssueError.message.slice(0, 500),
            })
            .eq("feature_request_id", featureRequestId);

          if (updateError) {
            console.error(
              "[useSubmitFeatureRequest] Failed to persist feature GitHub issue error",
              updateError,
            );
          }
        }
      }

      return payload;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: FEATURE_REQUESTS_QUERY_KEY });
    },
  });
}

export function useBugReportsReviewQueue() {
  const { user, roles = [] } = useAuth();
  const canReview = hasBugReportReviewRole(roles);

  return useQuery({
    queryKey: BUG_REPORTS_QUERY_KEY,
    queryFn: async (): Promise<BugReportReviewItem[]> => {
      if (!user || !canReview) return [];

      const { data, error } = await supabase
        .from("bug_reports")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) throw error;

      const rows = (data ?? []) as BugReportRow[];
      if (!rows.length) return [];

      const reporterIds = Array.from(new Set(rows.map((row) => row.reporter_user_id).filter(Boolean)));
      const reporterProfilesById = new Map<string, ReporterProfileRow>();

      if (reporterIds.length > 0) {
        const { data: reporterProfiles, error: reporterProfilesError } = await supabase
          .from("profiles")
          .select("id, full_name, email")
          .in("id", reporterIds);

        if (reporterProfilesError) {
          console.warn("[useBugReportsReviewQueue] Failed to load reporter profiles", reporterProfilesError);
        } else {
          for (const profile of (reporterProfiles ?? []) as ReporterProfileRow[]) {
            reporterProfilesById.set(profile.id, profile);
          }
        }
      }

      const { data: runs, error: runsError } = await supabase
        .from("bug_fix_runs")
        .select("id,bug_report_id,canonical_bug_report_id,status,created_at,updated_at")
        .or(`bug_report_id.in.(${rows.map((row) => row.id).join(",")}),canonical_bug_report_id.in.(${rows.map((row) => row.id).join(",")})`)
        .order("updated_at", { ascending: false });

      if (runsError) throw runsError;

      const latestRunsByReportId = new Map<string, BugFixRunRow>();
      for (const run of (runs ?? []) as BugFixRunRow[]) {
        const candidateIds = [run.bug_report_id, run.canonical_bug_report_id].filter(Boolean) as string[];
        for (const reportId of candidateIds) {
          if (!latestRunsByReportId.has(reportId)) {
            latestRunsByReportId.set(reportId, run);
          }
        }
      }

      return rows.map((row) =>
        mapBugReportRow(
          row,
          latestRunsByReportId.get(row.id) ?? null,
          reporterProfilesById.get(row.reporter_user_id) ?? null,
        ),
      );
    },
    enabled: !!user && canReview,
  });
}

export function useFeatureRequestsReviewQueue() {
  const { user, roles = [] } = useAuth();
  const canReview = hasInternalReviewRole(roles);

  return useQuery({
    queryKey: FEATURE_REQUESTS_QUERY_KEY,
    queryFn: async (): Promise<FeatureRequestReviewItem[]> => {
      if (!user || !canReview) return [];

      const { data, error } = await supabase
        .from("feature_requests")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) throw error;

      const rows = (data ?? []) as FeatureRequestRow[];
      if (!rows.length) return [];

      const { data: runs, error: runsError } = await supabase
        .from("feature_request_runs")
        .select("*")
        .in("feature_request_id", rows.map((row) => row.id))
        .order("created_at", { ascending: false });

      if (runsError) throw runsError;

      const latestRunsByRequestId = new Map<string, FeatureRequestRunRow>();
      for (const run of (runs ?? []) as FeatureRequestRunRow[]) {
        if (!latestRunsByRequestId.has(run.feature_request_id)) {
          latestRunsByRequestId.set(run.feature_request_id, run);
        }
      }

      return rows.map((row) => mapFeatureRequestRow(row, latestRunsByRequestId.get(row.id) ?? null));
    },
    enabled: !!user && canReview,
  });
}

export function useDecideFeatureRequest() {
  const queryClient = useQueryClient();
  const { user, roles = [] } = useAuth();

  return useMutation({
    mutationFn: async ({ id, decisionStatus, decisionNotes }: DecideFeatureRequestInput) => {
      if (!user) throw new Error("Not authenticated");
      if (!hasInternalReviewRole(roles)) throw new Error("Not authorized to review feature requests");

      if (decisionStatus === "go") {
        const { error: invokeError } = await supabase.functions.invoke("promote-feature-request", {
          body: {
            featureRequestId: id,
            decisionNotes: decisionNotes?.trim() ? decisionNotes.trim() : null,
          },
        });

        if (invokeError) throw invokeError;
      } else {
        const { error } = await supabase
          .from("feature_requests")
          .update({
            decision_status: decisionStatus,
            decision_notes: decisionNotes?.trim() ? decisionNotes.trim() : null,
            decided_by: user.id,
            decided_at: new Date().toISOString(),
          })
          .eq("id", id);

        if (error) throw error;

        const { error: runUpdateError } = await supabase
          .from("feature_request_runs")
          .update({
            status: "no-go",
            failure_reason: null,
          })
          .eq("feature_request_id", id);

        if (runUpdateError) {
          console.error("[useDecideFeatureRequest] Failed to update feature request run after no-go", runUpdateError);
        }
      }

      return { id, decisionStatus, decisionNotes: decisionNotes ?? null };
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: FEATURE_REQUESTS_QUERY_KEY });
    },
  });
}
